import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Download, DatabaseBackup } from "lucide-react";
import { gapi, errMsg, fmtDateTime, backendUrl } from "../api";
import { PageHeader, Panel, Btn, Badge } from "../components/ui";

function Agenzia() {
  const [a, setA] = useState(null);
  useEffect(() => { gapi.get("/settings/agenzia").then((r) => setA(r.data)).catch(() => {}); }, []);
  if (!a) return null;
  const save = async () => { try { setA((await gapi.put("/settings/agenzia", a)).data); toast.success("Recapiti agenzia salvati"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Panel title="Recapiti agenzia (brochure)" testid="agency-settings">
      <div className="grid sm:grid-cols-2 gap-2">{[["nome", "Nome"], ["slogan", "Slogan"], ["indirizzo", "Indirizzo"], ["telefono", "Telefono"], ["email", "Email"], ["sito", "Sito"]].map(([k, l]) => (
        <label key={k} className="text-xs">{l}<input className="mt-1 h-10 w-full rounded-xl border border-border-subtle px-3 text-sm" value={a[k] || ""} onChange={(e) => setA({ ...a, [k]: e.target.value })} data-testid={`agency-${k}`} /></label>))}</div>
      <Btn className="mt-3" onClick={save} data-testid="agency-save">Salva recapiti</Btn>
    </Panel>
  );
}

function Privacy() {
  const [c, setC] = useState(null);
  useEffect(() => { gapi.get("/settings/privacy").then((r) => setC(r.data)); }, []);
  if (!c) return null;
  const save = async () => { try { const { data } = await gapi.put("/settings/privacy", c); setC(data); toast.success("Impostazioni privacy salvate"); } catch (e) { toast.error(errMsg(e)); } };
  const n = (k) => (e) => setC({ ...c, [k]: Number(e.target.value) });
  return (
    <Panel title="Privacy e conservazione dei dati" className="lg:col-span-3" testid="g-privacy-settings" actions={<Btn onClick={save} data-testid="g-privacy-save">Salva</Btn>}>
      <div className="grid sm:grid-cols-3 gap-3 text-sm">
        <label>Conservazione contatti senza attività (mesi)<input type="number" min="1" className="mt-1 w-full h-11 rounded-xl border border-border-subtle px-3" value={c.mesiConservazioneContattiInattivi} onChange={n("mesiConservazioneContattiInattivi")} data-testid="g-privacy-months" /></label>
        <label>Conservazione richieste dal sito (mesi)<input type="number" min="1" className="mt-1 w-full h-11 rounded-xl border border-border-subtle px-3" value={c.mesiConservazioneRichiesteSito} onChange={n("mesiConservazioneRichiesteSito")} /></label>
        <label>Versione informativa in uso<input className="mt-1 w-full h-11 rounded-xl border border-border-subtle px-3" value={c.versioneInformativa} onChange={(e) => setC({ ...c, versioneInformativa: e.target.value })} /></label>
      </div>
      <p className="mt-3 text-sm"><b data-testid="g-privacy-over">{c.contattiOltreConservazione}</b> contatti superano il periodo di conservazione impostato: valuta se archiviarli o eliminarli (l'eliminazione definitiva è riservata al titolare e non avviene in automatico).</p>
      <p className="text-xs text-anthracite/45 mt-2">Il gestionale fornisce strumenti per gestire informative, consensi e tempi di conservazione; la valutazione di conformità resta in capo all'agenzia.</p>
    </Panel>
  );
}

function Modelli() {
  const [c, setC] = useState(null);
  useEffect(() => { gapi.get("/settings/fase3").then((r) => setC(r.data)); }, []);
  if (!c) return null;
  const save = async () => { try { const { data } = await gapi.put("/settings/fase3", c); setC(data); toast.success("Modelli salvati"); } catch (e) { toast.error(errMsg(e)); } };
  const area = "mt-1 w-full rounded-xl border border-border-subtle px-3 py-2 text-sm";
  return (
    <Panel title="Fasi, checklist e modelli dell'agenzia" className="lg:col-span-3" testid="g-models-settings" actions={<Btn onClick={save} data-testid="g-models-save">Salva</Btn>}>
      <div className="grid sm:grid-cols-3 gap-3 mt-3">{Object.keys(c.checklist).map((k) => (
        <label key={k} className="block text-sm capitalize">Checklist {k} (una voce per riga)<textarea rows={6} className={area} value={c.checklist[k].join("\n")} onChange={(e) => setC({ ...c, checklist: { ...c.checklist, [k]: e.target.value.split("\n").filter((x) => x.trim()) } })} /></label>))}</div>
      <label className="block text-sm mt-3">Testo approvato del foglio visita<textarea rows={3} className={area} value={c.modelloFoglioVisita} onChange={(e) => setC({ ...c, modelloFoglioVisita: e.target.value })} data-testid="g-models-foglio" /></label>
    </Panel>
  );
}

const kb = (n) => `${(n / 1024).toFixed(1)} KB`;

export default function Sistema() {
  const [info, setInfo] = useState(null);
  const [backups, setBackups] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    gapi.get("/system/info").then((r) => setInfo(r.data));
    gapi.get("/system/backups").then((r) => setBackups(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try { const { data } = await gapi.post("/system/backups"); toast[data.verificato ? "success" : "warning"](data.esitoVerifica); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="g-system-page">
      <div className="mb-6"><Agenzia /></div>
      <PageHeader eyebrow="Amministrazione" title="Sistema e backup"
        desc="Ogni backup del database viene salvato in archivio privato e verificato con un ripristino di prova su un database separato."
        actions={<Btn onClick={create} disabled={busy} data-testid="g-backup-create"><DatabaseBackup className="h-4 w-4" />{busy ? "Backup in corso…" : "Crea backup ora"}</Btn>} />
      <div className="grid lg:grid-cols-3 gap-5">
        <Panel title="Ambiente" testid="g-system-env">
          {info && (
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-anthracite/50">Ambiente</dt><dd><Badge tone={info.ambiente === "prova" ? "amber" : "green"}>{info.ambiente === "prova" ? "Prova" : "Produzione"}</Badge></dd></div>
              <div className="flex justify-between"><dt className="text-anthracite/50">Fuso orario</dt><dd>{info.fusoOrario}</dd></div>
              <div className="flex justify-between"><dt className="text-anthracite/50">Valuta</dt><dd>Euro (€)</dd></div>
            </dl>
          )}
          <p className="text-xs text-anthracite/45 mt-4">Prova e produzione usano database separati. Eventuali dati dimostrativi verranno creati solo nell'ambiente di prova.</p>
        </Panel>
        <Panel title="Migrazioni applicate" className="lg:col-span-2" testid="g-system-migrations">
          <ul className="text-sm divide-y divide-border-subtle">
            {(info?.migrazioni || []).map((m) => <li key={m.id} className="py-2"><b>{m.id}</b> · {m.descrizione}<br /><span className="text-xs text-anthracite/45">Applicata il {fmtDateTime(m.appliedAt)} · backup preventivo {m.backupId ? "eseguito" : "non disponibile"}</span></li>)}
          </ul>
        </Panel>
        <Privacy />
        <Modelli />
        <Panel title="Backup" className="lg:col-span-3" testid="g-backup-list">
          {!backups.length ? <p className="text-sm text-anthracite/45">Nessun backup.</p> : (
            <ul className="divide-y divide-border-subtle text-sm">
              {backups.map((b) => (
                <li key={b.id} className="py-3 flex flex-wrap gap-3 items-center justify-between">
                  <span><b>{fmtDateTime(b.createdAt)}</b> · {b.etichetta} · {kb(b.dimensione)} · {b.creatoDaNome}<br /><span className="text-xs text-anthracite/50">{b.esitoVerifica}</span></span>
                  <span className="flex items-center gap-2">
                    <Badge tone={b.verificato ? "green" : "amber"}>{b.verificato ? "Ripristino verificato" : "Non verificato"}</Badge>
                    {b.path && <a href={backendUrl(`/api/g/system/backups/${b.id}/download`)} className="inline-flex items-center gap-1 text-brand font-medium" data-testid={`g-backup-download-${b.id}`}><Download className="h-4 w-4" /> Scarica</a>}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-anthracite/45 mt-4">I file caricati (foto e documenti) sono conservati nell'archivio oggetti persistente; il backup include l'indice dei file. Il ripristino completo si esegue con la procedura tecnica documentata (comando di ripristino su un database di destinazione), riservata al titolare.</p>
        </Panel>
      </div>
    </div>
  );
}
